// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir
import _ from 'lodash';

import {
  QuoteTerminatingAction,
  buildUserConfirmedQuoteBody,
  quoteValueTypeToApi,
  updateQuoteRequestSuggestion,
} from 'lib/api/updateQuoteRequestSuggestion';
import { Maybe } from 'types/UtilityTypes';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import captureException from 'utils/captureException';

import { QuoteValueType } from '../../constants';
import { CarrierCostType, LTLQuote, ProfitType } from '../../types';
import { getLTLSellRate } from '../../util';

export type BatchLTLFinalQuote = {
  key: number;
  quoteRequestId: number;
  selectedQuotes: LTLQuote[];
};

export type BatchLTLFinalQuoteResult = {
  succeeded: BatchLTLFinalQuote[];
  failed: BatchLTLFinalQuote[];
};

type SubmitBatchLTLFinalQuotesParams = {
  finalQuotes: BatchLTLFinalQuote[];
  customerExternalTMSId: Maybe<string>;
  terminatingAction: QuoteTerminatingAction;
};

// Marks each quoted shipment Accepted with its cheapest selected rate, as the single LTL
// form does on draft/send/copy: total sell, the terminating action and the selected LTL
// quick quote ids. Single LTL's sendUserQuote is a no-op for LTL (it needs an FTL
// Greenscreens quote), so batch does not call it. Returns the shipments it quoted.
export async function submitBatchLTLFinalQuotes({
  finalQuotes,
  customerExternalTMSId,
  terminatingAction,
}: SubmitBatchLTLFinalQuotesParams): Promise<BatchLTLFinalQuoteResult> {
  const quoted = finalQuotes.filter(
    ({ quoteRequestId, selectedQuotes }) =>
      quoteRequestId > 0 && selectedQuotes.length > 0
  );

  const results = await Promise.all(
    quoted.map(async (quote) => {
      try {
        const [cheapest] = [...quote.selectedQuotes].sort(
          (a, b) => getLTLSellRate(a) - getLTLSellRate(b)
        );
        const sellRate = getLTLSellRate(cheapest);
        const carrierCost =
          cheapest.rateMetadata?.buyRateTotal ?? cheapest.totalCost;

        const result = await updateQuoteRequestSuggestion(
          quote.quoteRequestId,
          SuggestionStatus.Accepted,
          buildUserConfirmedQuoteBody({
            finalQuotePrice: _.round(sellRate),
            finalMargin: _.round(sellRate - carrierCost),
            marginType: ProfitType.Amount,
            finalCarrierCost: _.round(carrierCost),
            carrierCostType: CarrierCostType.Flat,
            customerExternalTMSId: customerExternalTMSId ?? undefined,
            quoteTerminatingAction: terminatingAction,
            quotedValueType: quoteValueTypeToApi(QuoteValueType.TotalSell),
          }),
          undefined,
          quote.selectedQuotes
            .map((selectedQuote) => selectedQuote.id)
            .filter((id) => id > 0)
        );

        if (result.isErr()) {
          captureException(result.error, {
            functionName: 'submitBatchLTLFinalQuotes',
          });
          return { quote, succeeded: false };
        }

        return { quote, succeeded: true };
      } catch (error) {
        captureException(error, {
          functionName: 'submitBatchLTLFinalQuotes',
        });
        return { quote, succeeded: false };
      }
    })
  );

  return {
    succeeded: results
      .filter(({ succeeded }) => succeeded)
      .map(({ quote }) => quote),
    failed: results
      .filter(({ succeeded }) => !succeeded)
      .map(({ quote }) => quote),
  };
}
